import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type MouseEvent,
} from 'react';
import {
  BookAIcon,
  Calendar,
  CalendarCheck,
  LogOut,
  Menu,
  User,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getNameFromObj, getUserInitials } from '@gokrew/ui/utility/utility';
import { UserMenuDropdown } from '@gokrew/ui/components/UserMenuDropdown';
import { ConfirmationDialog } from '@gokrew/ui/components/ConfirmationDialog';
import { useDialog } from '@gokrew/ui/context';
import { useTourContext } from '@gokrew/ui';
import { useMediaQuery } from '@gokrew/ui/hooks';
import { TranslationNS } from '@gokrew/models/constants';
import { useEmployeePortalAuth } from '@/context/EmployeeAuthContext';
import Notifications from '@/components/Notifications';
import styles from './EmployeeHeader.module.css';
import { Language_Options } from '@gokrew/ui/constants/language-options';
import { useUpdateEmployeeLanguage } from '@/hooks/useUpdateEmployeeLanguage';
import { useBreadcrumbs } from '@/hooks/useBreadcrumbs';
import {
  GLOW_EASING,
  GLOW_MAX_STEP,
  GLOW_SETTLE,
  glowDistanceFor,
  glowPerimeter,
  glowPointAt,
  prefersReducedMotion,
} from '@gokrew/ui/utility/headerGlow';

interface EmployeeHeaderProps {
  onMenuClick?: () => void;
}

const WELCOME_MESSAGE_COUNT = 5;
const WELCOME_ROTATE_MS = 6000;
const WELCOME_FADE_MS = 400;

export function EmployeeHeader({ onMenuClick }: EmployeeHeaderProps) {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation(TranslationNS.Common);
  const { t: tNav } = useTranslation(TranslationNS.Sidebar);
  const breadcrumbs = useBreadcrumbs();
  const tCrumb = useCallback(
    (crumb: string) => tNav(`nav.${crumb}`, { defaultValue: crumb }),
    [tNav]
  );
  const { tours } = useTourContext();
  const { logout, user, isMockLogin, endMockSession } = useEmployeePortalAuth();

  // Phones only show the avatar; the greeting already names the user.
  const isCompact = useMediaQuery('(max-width: 640px)');
  const employeeName =
    user?.user && !isCompact ? getNameFromObj(user.user) : undefined;
  // No job title: show nothing rather than a bare "N/A" under the name.
  const employeeSubtitle = isCompact
    ? undefined
    : user?.designation?.name || undefined;
  const { openDialog } = useDialog();
  const currentLang = i18n.language?.trim() || 'en';
  const [open, setOpen] = useState(false);
  const { mutate: saveLanguage } = useUpdateEmployeeLanguage();

  const [welcomeIndex, setWelcomeIndex] = useState(0);
  const [isWelcomeVisible, setIsWelcomeVisible] = useState(true);

  useEffect(() => {
    let swapTimer: ReturnType<typeof setTimeout>;

    const rotateTimer = setInterval(() => {
      setIsWelcomeVisible(false);

      swapTimer = setTimeout(() => {
        setWelcomeIndex((index) => (index + 1) % WELCOME_MESSAGE_COUNT);
        setIsWelcomeVisible(true);
      }, WELCOME_FADE_MS);
    }, WELCOME_ROTATE_MS);

    return () => {
      clearInterval(rotateTimer);
      clearTimeout(swapTimer);
    };
  }, []);

  const handleLanguageChange = useCallback(
    (code: string) => {
      saveLanguage(code);
    },
    [saveLanguage]
  );

  const haloRef = useRef<HTMLSpanElement>(null);
  const glow = useRef({
    at: 0,
    to: 0,
    frame: 0,
    isEntering: true,
    width: 0,
    height: 0,
    perimeter: 0,
    originX: 0,
    originY: 0,
  });

  const runGlowFrame = useCallback(() => {
    const halo = haloRef.current;
    if (!halo) return;

    const state = glow.current;

    // Take whichever way round the pill is shorter, so crossing between the
    // edges sweeps past the nearer cap instead of cutting through the middle.
    let delta = state.to - state.at;
    if (delta > state.perimeter / 2) delta -= state.perimeter;
    if (delta < -state.perimeter / 2) delta += state.perimeter;

    const isSettled = Math.abs(delta) < GLOW_SETTLE;
    const step = Math.max(
      -GLOW_MAX_STEP,
      Math.min(GLOW_MAX_STEP, delta * GLOW_EASING)
    );
    state.at = isSettled
      ? state.to
      : (state.at + step + state.perimeter) % state.perimeter;

    const point = glowPointAt(state.at, state.width, state.height);
    halo.style.setProperty('--glow-x', `${point.x}px`);
    halo.style.setProperty('--glow-y', `${point.y}px`);

    state.frame = isSettled ? 0 : requestAnimationFrame(runGlowFrame);
  }, []);

  const handleGlowMove = useCallback(
    (event: MouseEvent) => {
      const halo = haloRef.current;
      // The glow is hidden behind the open menu, so there is nothing to drive.
      if (!halo || open) return;

      const state = glow.current;

      if (state.isEntering) {
        const rect = halo.getBoundingClientRect();
        state.width = rect.width;
        state.height = rect.height;
        state.perimeter = glowPerimeter(rect.width, rect.height);
        state.originX = rect.left;
        state.originY = rect.top;
      }

      state.to = glowDistanceFor(
        event.clientX - state.originX,
        state.width,
        state.height,
        event.clientY - state.originY < state.height / 2
      );

      if (state.isEntering || prefersReducedMotion()) {
        state.isEntering = false;
        state.at = state.to;
      }

      if (!state.frame) state.frame = requestAnimationFrame(runGlowFrame);
    },
    [open, runGlowFrame]
  );

  const stopGlow = useCallback(() => {
    const state = glow.current;
    if (state.frame) cancelAnimationFrame(state.frame);
    state.frame = 0;
    state.isEntering = true;
  }, []);

  useEffect(() => stopGlow, [stopGlow]);

  const openLogoutConfirmationDialog = () => {
    setOpen(false);
    openDialog({
      title: t('top_nav.logout_dialog.title'),
      content: (
        <ConfirmationDialog onSubmit={logout}>
          <p>{t('top_nav.logout_dialog.message')}</p>
        </ConfirmationDialog>
      ),
    });
  };

  const locale = currentLang === 'fr' ? 'fr-CA' : currentLang;
  const today = new Intl.DateTimeFormat(locale, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(new Date());

  const dateLine = (
    <span className={styles.welcomeDate}>
      <Calendar
        size={14}
        strokeWidth={2}
        className={styles.welcomeDateIcon}
        aria-hidden="true"
      />
      {t('top_nav.today', { date: today })}
    </span>
  );

  return (
    <header className={styles.header}>
      <div className={styles.left}>
        <button
          className={styles.menuBtn}
          onClick={onMenuClick}
          aria-label={t('top_nav.open_menu')}
        >
          <Menu size={22} />
        </button>

        <div className={styles.welcome}>
          {isMockLogin ? (
            <h1 className={styles.welcomeTitle}>
              {t('top_nav.impersonating_as', {
                name: getNameFromObj(user?.user),
              })}
            </h1>
          ) : breadcrumbs ? (
            <>
              <h1 className={styles.welcomeTitle}>
                {tCrumb(breadcrumbs.pageCrumb)}
              </h1>
              {!breadcrumbs.crumbs[1] ? (
                dateLine
              ) : (
                <div className={styles.breadcrumbs}>
                  <button
                    type="button"
                    className={styles.breadcrumbSection}
                    onClick={() => navigate(breadcrumbs.rootPath)}
                  >
                    {tCrumb(breadcrumbs.crumbs[0])}
                  </button>
                  <span className={styles.breadcrumbSeparator}>•</span>
                  {breadcrumbs.crumbs[2] ? (
                    <>
                      <button
                        type="button"
                        className={styles.breadcrumbSection}
                        onClick={() =>
                          breadcrumbs.parentPath &&
                          navigate(breadcrumbs.parentPath)
                        }
                      >
                        {tCrumb(breadcrumbs.crumbs[1])}
                      </button>
                      <span className={styles.breadcrumbSeparator}>•</span>
                      <span className={styles.breadcrumbPage}>
                        {tCrumb(breadcrumbs.crumbs[2])}
                      </span>
                    </>
                  ) : (
                    <span className={styles.breadcrumbPage}>
                      {tCrumb(breadcrumbs.crumbs[1])}
                    </span>
                  )}
                </div>
              )}
            </>
          ) : (
            <>
              <h1 className={styles.welcomeTitle}>
                <span
                  className={`${styles.welcomeMessage} ${
                    isWelcomeVisible ? styles.welcomeMessageVisible : ''
                  }`}
                >
                  {t(`top_nav.welcome_messages.${welcomeIndex}`, {
                    name: getNameFromObj(user?.user) || 'User',
                  })}
                </span>
              </h1>
              {dateLine}
            </>
          )}
        </div>
      </div>

      <div className={styles.right}>
        <div className={styles.notificationsSlot}>
          <Notifications />
        </div>
        {tours.length > 0 && (
          <button
            type="button"
            className={styles.tourBtn}
            onClick={tours[0].start}
            aria-label={tours[0].label}
            title={tours[0].label}
          >
            <span className={styles.tourBtnMark} aria-hidden="true">
              ?
            </span>
          </button>
        )}
        {tours.length > 0 && (
          <span className={styles.separator} aria-hidden="true" />
        )}
        {isMockLogin && (
          <button
            type="button"
            onClick={() => {
              // Navigate first (client-side, to a public route) so the expired
              // page renders immediately. Clearing auth state afterwards avoids a
              // hard reload — which would flash the bootstrap AppLoaderPage.
              navigate('/mock-session-expired?ended=true');
              endMockSession();
            }}
            className={styles.mockSessionBtn}
          >
            <LogOut size={14} />
            {t('top_nav.end_mock_session')}
          </button>
        )}
        <div
          className={`${styles.actions} ${open ? styles.actionsOpen : ''}`}
          onMouseMove={handleGlowMove}
          onMouseLeave={stopGlow}
        >
          <span className={styles.glowHalo} ref={haloRef} aria-hidden="true">
            <span className={styles.glow} />
          </span>
          <UserMenuDropdown
            imageSrc={user?.user?.profileImage}
            initials={user?.user ? getUserInitials(user.user) : ''}
            triggerLabel={t('top_nav.user_menu')}
            name={employeeName}
            subtitle={employeeSubtitle}
            avatarPosition="start"
            showChevron
            avatarClassName={styles.avatar}
            triggerClassName={styles.userMenuTrigger}
            nameClassName={styles.userName}
            subtitleClassName={styles.userRole}
            chevronClassName={styles.userMenuChevron}
            open={open}
            onOpenChange={setOpen}
            contentClassName={styles.dropdownContent}
            itemClassName={styles.dropdownItem}
            dangerItemClassName={`${styles.dropdownItem} ${styles.dropdownItemDanger}`}
            items={[
              {
                label: t('top_nav.profile'),
                icon: User,
                onSelect: () => navigate('/my-information'),
              },
              {
                label: t('top_nav.my_calendar'),
                icon: CalendarCheck,
                onSelect: () => navigate('/calendar-connection'),
              },
              {
                type: 'submenu',
                label: t('top_nav.languages'),
                icon: BookAIcon,
                subItems: Language_Options,
                activeSubItemCode: currentLang,
                onSubItemSelect: handleLanguageChange,
              },
              {
                label: t('top_nav.logout'),
                icon: LogOut,
                variant: 'danger',
                dividerBefore: true,
                onSelect: openLogoutConfirmationDialog,
              },
            ]}
          />
        </div>
      </div>
    </header>
  );
}
